/**
 * Facebook Messenger Platform - Live Videos Webhook Types
 *
 * These types represent the webhook event structure for live video events.
 * Triggered when there are changes to a page's live video status, such as
 * when a live video starts, ends, or has status updates.
 *
 * @see https://developers.facebook.com/docs/graph-api/webhooks/reference/page/#live_videos
 */

import { PageWebhookPayload } from './base-types';

/**
 * Enumeration of live video broadcast statuses
 * Based on Facebook Graph API BroadcastStatus enum
 *
 * @see https://developers.facebook.com/docs/graph-api/reference/live-video/
 */
export enum LiveVideoStatus {
  /** Live broadcast is currently streaming */
  LIVE = 'LIVE',

  /** Live broadcast has stopped */
  LIVE_STOPPED = 'LIVE_STOPPED',

  /** Live broadcast is currently being processed */
  PROCESSING = 'PROCESSING',

  /** Scheduled broadcast was canceled */
  SCHEDULED_CANCELED = 'SCHEDULED_CANCELED',

  /** Scheduled broadcast has expired */
  SCHEDULED_EXPIRED = 'SCHEDULED_EXPIRED',

  /** Live broadcast is scheduled and published */
  SCHEDULED_LIVE = 'SCHEDULED_LIVE',

  /** Live broadcast is scheduled but unpublished */
  SCHEDULED_UNPUBLISHED = 'SCHEDULED_UNPUBLISHED',

  /** Live broadcast is unpublished */
  UNPUBLISHED = 'UNPUBLISHED',

  /** Live broadcast video on demand (VOD) is ready */
  VOD = 'VOD',
}

/**
 * Live video event value data
 */
export interface LiveVideoEventValue {
  /** The ID of the live video */
  id: string;

  /** Status of the live video (enum value directly) */
  status: LiveVideoStatus;
}

/**
 * Main webhook event structure for live videos
 *
 * This event is triggered when there are changes to a page's live video status.
 * The webhook provides information about the live video state changes.
 *
 * @example Live video started:
 * ```json
 * {
 *   "field": "live_videos",
 *   "value": {
 *     "id": "123456789",
 *     "status": "LIVE"
 *   }
 * }
 * ```
 *
 * @example Live video ended:
 * ```json
 * {
 *   "field": "live_videos",
 *   "value": {
 *     "id": "123456789",
 *     "status": "LIVE_STOPPED"
 *   }
 * }
 * ```
 */
export interface LiveVideoWebhookEvent {
  /** Name of the updated field */
  field: 'live_videos';

  /** The contents of the update */
  value: LiveVideoEventValue;
}

/**
 * Complete webhook payload structure for live video events
 */
export interface LiveVideoWebhookPayload extends PageWebhookPayload<LiveVideoWebhookEvent> {}

/**
 * Type guard to check if a webhook event is a live video event
 *
 * @param event - The webhook event to check
 * @returns True if the event is a live video event
 *
 * @example
 * ```typescript
 * if (isLiveVideoEvent(event)) {
 *   console.log(`Live video ${event.value.id} status: ${event.value.status}`);
 * }
 * ```
 */
export function isLiveVideoEvent(event: any): event is LiveVideoWebhookEvent {
  return event && typeof event === 'object' && event.field === 'live_videos';
}

/**
 * Type guard to check if a live video is currently live
 *
 * @param value - The live video event value to check
 * @returns True if the live video is currently streaming
 */
export function isLive(value: LiveVideoEventValue): boolean {
  return value.status === LiveVideoStatus.LIVE;
}

/**
 * Type guard to check if a live video is scheduled
 *
 * @param value - The live video event value to check
 * @returns True if the live video is scheduled
 */
export function isScheduled(value: LiveVideoEventValue): boolean {
  return (
    value.status === LiveVideoStatus.SCHEDULED_UNPUBLISHED ||
    value.status === LiveVideoStatus.SCHEDULED_LIVE
  );
}

/**
 * Type guard to check if a live video is processing
 *
 * @param value - The live video event value to check
 * @returns True if the live video is being processed
 */
export function isProcessing(value: LiveVideoEventValue): boolean {
  return value.status === LiveVideoStatus.PROCESSING;
}

/**
 * Type guard to check if a live video has ended
 *
 * @param value - The live video event value to check
 * @returns True if the live video has stopped, was cancelled, or expired
 */
export function hasEnded(value: LiveVideoEventValue): boolean {
  return (
    value.status === LiveVideoStatus.LIVE_STOPPED ||
    value.status === LiveVideoStatus.SCHEDULED_CANCELED ||
    value.status === LiveVideoStatus.SCHEDULED_EXPIRED
  );
}

/**
 * Type guard to check if a live video VOD is ready
 *
 * @param value - The live video event value to check
 * @returns True if the video on demand is ready
 */
export function isVODReady(value: LiveVideoEventValue): boolean {
  return value.status === LiveVideoStatus.VOD;
}

/**
 * Utility type for extracting just the live video event data
 */
export type LiveVideoEventData = LiveVideoEventValue;

/**
 * Processing context for live video events
 */
export interface LiveVideoProcessingContext {
  /** The page ID */
  pageId: string;

  /** When the event occurred */
  timestamp: number;

  /** Human-readable datetime for the event timestamp */
  eventDate: Date;

  /** The live video ID */
  videoId: string;

  /** Current video status */
  status: LiveVideoStatus;

  /** Whether the video is currently live */
  isLive: boolean;

  /** Whether the video is scheduled */
  isScheduled: boolean;

  /** Whether the video is being processed */
  isProcessing: boolean;

  /** Whether the video has ended */
  hasEnded: boolean;

  /** Whether the VOD is ready */
  isVODReady: boolean;
}

/**
 * Helper function to extract processing context from a live video event
 *
 * @param pageId - The page ID from the webhook entry
 * @param timestamp - The timestamp from the webhook entry
 * @param event - The live video webhook event
 * @returns Simplified processing context
 *
 * @example
 * ```typescript
 * const context = extractLiveVideoContext(entry.id, entry.time, event);
 * console.log(`Live video ${context.videoId} is ${context.status}`);
 * if (context.isLive) {
 *   console.log('Stream is currently live!');
 * } else if (context.hasEnded) {
 *   console.log('Stream has ended');
 * }
 * ```
 */
export function extractLiveVideoContext(
  pageId: string,
  timestamp: number,
  event: LiveVideoWebhookEvent
): LiveVideoProcessingContext {
  const { value } = event;

  return {
    pageId,
    timestamp,
    eventDate: new Date(timestamp),
    videoId: value.id,
    status: value.status,
    isLive: isLive(value),
    isScheduled: isScheduled(value),
    isProcessing: isProcessing(value),
    hasEnded: hasEnded(value),
    isVODReady: isVODReady(value),
  };
}

/**
 * Helper function to determine if a status change is a transition to live
 *
 * @param fromStatus - The previous status
 * @param toStatus - The new status
 * @returns True if this represents going live
 *
 * @example
 * ```typescript
 * if (isGoingLive(previousStatus, event.value.status)) {
 *   console.log('Video just went live!');
 * }
 * ```
 */
export function isGoingLive(fromStatus: LiveVideoStatus | undefined, toStatus: LiveVideoStatus): boolean {
  return fromStatus !== LiveVideoStatus.LIVE && toStatus === LiveVideoStatus.LIVE;
}

/**
 * Helper function to determine if a status change is a transition from live to ended
 *
 * @param fromStatus - The previous status
 * @param toStatus - The new status
 * @returns True if this represents ending a live stream
 *
 * @example
 * ```typescript
 * if (isEndingLive(previousStatus, event.value.status)) {
 *   console.log('Live stream has ended');
 * }
 * ```
 */
export function isEndingLive(fromStatus: LiveVideoStatus | undefined, toStatus: LiveVideoStatus): boolean {
  return (
    fromStatus === LiveVideoStatus.LIVE &&
    (toStatus === LiveVideoStatus.LIVE_STOPPED ||
     toStatus === LiveVideoStatus.SCHEDULED_CANCELED ||
     toStatus === LiveVideoStatus.SCHEDULED_EXPIRED)
  );
}

/**
 * Constants related to live video events
 */
export const LIVE_VIDEO_CONSTANTS = {
  /** Webhook field name */
  FIELD_NAME: 'live_videos' as const,

  /** All possible video statuses */
  STATUSES: Object.values(LiveVideoStatus),
} as const;
